<script setup>
import { computed, ref, watch, reactive, nextTick } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { dataHojeBR } from '@/utils/useDataBR'
import { FIN_ST_SOL_ADT } from '@/constants/FIN_ST_SOL_ADT'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const modelValueComputed = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  modo: { type: String, default: 'criacao' }, // 'criacao' | 'edicao' | 'visualizacao'
  solicitacao: { type: Object, default: null },
  idEmbarque: { type: Number, default: null },
  idOrganizacao: { type: Number, default: null },
  listas: { type: Object, default: () => ({}) }
})

const emit = defineEmits([
  'update:modelValue',
  'salvar',
  'show-notification'
])

// ============================================================
// ESTADO DO FORMULÁRIO
// ============================================================
const formData = ref(null)
const isLoadingListas = ref(false)
const isSaving = ref(false)

const isVisualizacao = computed(() => props.modo === 'visualizacao')
const podeEditar = computed(() => !isVisualizacao.value)

const tituloDinamico = computed(() => {
  if (!formData.value) return 'Nova Solicitação de Adiantamento'
  if (isVisualizacao.value) return 'Visualizar Solicitação de Adiantamento'
  const base = formData.value.idSolicitacaoAdiantamento ? 'Editar' : 'Nova'
  const fornecedor = formData.value.nomeParceiro || ''
  return fornecedor ? `${base} — ${fornecedor}` : `${base} Solicitação de Adiantamento`
})

// ============================================================
// CONFIG BUSCA FORNECEDOR
// ============================================================
const configBuscaParceiro = computed(() => ({
  ...parceiroSearch,
  titulo: 'Buscar Fornecedor',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva, isFornecedor: true }
}))

// ============================================================
// ITENS (descrição livre + valor)
// ============================================================
const isModalItemOpen = ref(false)
const novoItemModelo = { descricao: '', valor: null }
const indiceItemEditando = ref(-1)
const modalItemParaEdicao = reactive({ ...novoItemModelo })

const abrirModalItem = (index = -1) => {
  if (isVisualizacao.value) return
  if (index === -1) {
    indiceItemEditando.value = -1
    Object.assign(modalItemParaEdicao, novoItemModelo)
  } else {
    indiceItemEditando.value = index
    Object.assign(modalItemParaEdicao, formData.value.itens[index])
  }
  isModalItemOpen.value = true
}

const salvarModalItem = () => {
  if (!modalItemParaEdicao.descricao) {
    emit('show-notification', { message: 'A descrição do item é obrigatória.', type: 'warning' })
    return
  }
  if (!modalItemParaEdicao.valor || modalItemParaEdicao.valor <= 0) {
    emit('show-notification', { message: 'O valor do item deve ser maior que zero.', type: 'warning' })
    return
  }
  const payloadItem = { ...modalItemParaEdicao }
  if (indiceItemEditando.value === -1) {
    formData.value.itens.push(payloadItem)
  } else {
    formData.value.itens.splice(indiceItemEditando.value, 1, payloadItem)
  }
  isModalItemOpen.value = false
}

const removerItem = (index) => {
  formData.value.itens.splice(index, 1)
}

const totalItens = computed(() => {
  if (!formData.value?.itens || !Array.isArray(formData.value.itens)) return 0
  return formData.value.itens.reduce((acc, current) => acc + (Number(current.valor) || 0), 0)
})

// ============================================================
// FORMATAR
// ============================================================
const formatarNumeroView = (valor, decimais = 2) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

// ============================================================
// WATCH — abre/fecha modal
// ============================================================
watch(() => props.modelValue, async (aberto) => {
  if (!aberto) {
    formData.value = null
    isSaving.value = false
    return
  }

  // Carrega listas
  isLoadingListas.value = true
  try {
    await metadataStore.carregarListasParaTabelas(['SolicitacaoAdiantamento'])
  } catch (e) {
    console.error('[SolicitacaoAdiantamentoModal] erro ao carregar listas:', e)
  } finally {
    isLoadingListas.value = false
  }

  if (props.solicitacao?.idSolicitacaoAdiantamento) {
    try {
      const res = await api.get(`/SolicitacoesAdiantamento/${props.solicitacao.idSolicitacaoAdiantamento}`)
      formData.value = res.data
    } catch (e) {
      emit('show-notification', { message: 'Erro ao carregar dados da solicitação.', type: 'error' })
      formData.value = { ...props.solicitacao }
    }
  } else if (props.solicitacao) {
    formData.value = { ...props.solicitacao }
  } else {
    formData.value = {
      idSolicitacaoAdiantamento: null,
      idOrganizacao: props.idOrganizacao || authStore.organizacaoAtiva,
      idParceiro: null,
      nomeParceiro: '',
      valorTotal: null,
      observacao: '',
      idEmbarque: props.idEmbarque,
      lstMoeda: '',
      dataVencimento: dataHojeBR(),
      numeroFaturaOriginal: '',
      lstFormaPagamento: '',
      codigoBarrasOuPix: '',
      lstStatusSolicitacao: FIN_ST_SOL_ADT.Pendente,
      itens: []
    }
  }

  nextTick(() => {
    const campo = document.getElementById('idParceiro')
    if (campo) {
      const input = campo.querySelector('input') || campo
      if (input && typeof input.focus === 'function') input.focus()
    }
  })
}, { immediate: true })

// ============================================================
// SALVAR
// ============================================================
const handleSalvar = async () => {
  if (isVisualizacao.value || !formData.value || isSaving.value) return
  isSaving.value = true

  if (!formData.value.idParceiro) {
    emit('show-notification', { message: 'Selecione um fornecedor.', type: 'warning' })
    isSaving.value = false
    return
  }
  if (!totalItens.value || totalItens.value <= 0) {
    emit('show-notification', { message: 'O valor total deve ser maior que zero.', type: 'warning' })
    isSaving.value = false
    return
  }
  if (!formData.value.lstMoeda) {
    emit('show-notification', { message: 'Selecione uma moeda.', type: 'warning' })
    isSaving.value = false
    return
  }

  const idOrganizacao = props.idOrganizacao || authStore.organizacaoAtiva
  const payload = {
    ...formData.value,
    valorTotal: totalItens.value,
    idOrganizacao: idOrganizacao ? Number(idOrganizacao) : null,
    idParceiro: formData.value.idParceiro ? Number(formData.value.idParceiro) : null,
    itens: formData.value.itens.map(i => ({
      descricao: i.descricao,
      valor: i.valor
    }))
  }

  emit('salvar', payload)
}

const handleFechar = () => {
  emit('update:modelValue', false)
}
</script>

<template>
  <ModalFormularioV2
    v-model="modelValueComputed"
    :loading="isLoadingListas"
    cor-header="var(--bs-info)"
    @confirmar="handleSalvar"
    @update:model-value="handleFechar"
    tamanho="lg"
  >
    <template #header>
      <div class="d-flex align-items-center gap-2">
        <i class="bi bi-cash-coin fs-5"></i>
        <span>{{ tituloDinamico }}</span>
      </div>
    </template>

    <div v-if="formData" class="solicitacao-adiantamento-modal">
      <!-- DADOS DA SOLICITAÇÃO -->
      <div class="row g-3">
        <div class="col-md-6">
          <CampoBusca
            id="idParceiro"
            label="Fornecedor"
            v-model="formData.idParceiro"
            :configuracao-busca="configBuscaParceiro"
            required
            :disabled="!podeEditar"
            @show-notification="(p) => emit('show-notification', p)"
          />
        </div>

        <div class="col-md-3">
          <CampoInput
            id="valorTotal"
            label="Valor Total"
            type="moeda"
            :value="totalItens"
            required
            disabled
          />
        </div>

        <div class="col-md-3">
          <CampoBuscaLista
            id="lstMoeda"
            label="Moeda"
            v-model="formData.lstMoeda"
            tabela="SolicitacaoAdiantamento"
            required
            :disabled="!podeEditar"
          />
        </div>

        <div class="col-md-3">
          <CampoInput
            id="dataVencimento"
            label="Data de Vencimento"
            type="date"
            v-model="formData.dataVencimento"
            required
            :disabled="!podeEditar"
          />
        </div>

        <div class="col-md-3">
          <CampoInput
            id="numeroFaturaOriginal"
            label="Doc / Fatura (Opcional)"
            v-model="formData.numeroFaturaOriginal"
            placeholder="Ex: Contrato 123"
            :disabled="!podeEditar"
          />
        </div>

        <div class="col-md-6">
          <CampoBuscaLista
            id="lstFormaPagamento"
            label="Forma de Pagamento"
            v-model="formData.lstFormaPagamento"
            tabela="SolicitacaoAdiantamento"
            :disabled="!podeEditar"
          />
        </div>

        <div class="col-md-12">
          <CampoInput
            id="codigoBarrasOuPix"
            label="Código Barras / Pix"
            v-model="formData.codigoBarrasOuPix"
            placeholder="Chave Pix ou linha digitável"
            :disabled="!podeEditar"
          />
        </div>

        <div class="col-md-12">
          <CampoInput
            id="observacao"
            label="Observações"
            type="textarea"
            v-model="formData.observacao"
            :readonly="!podeEditar"
            descricao-informativa="Máximo 200 caracteres"
          />
        </div>
      </div>

      <!-- ITENS -->
      <hr class="my-3" />
      <div class="d-flex align-items-center justify-content-between mb-2">
        <h6 class="mb-0 fw-bold"><i class="bi bi-list-check me-2"></i>Itens do Adiantamento</h6>
        <div class="d-flex align-items-center gap-2">
          <span class="badge bg-light text-primary fs-6">
            {{ formatarNumeroView(totalItens) }}
          </span>
          <button
            v-if="podeEditar"
            type="button"
            class="btn btn-sm btn-outline-primary py-0"
            @click="abrirModalItem(-1)"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>

      <div class="admin-table-container border rounded-3">
        <table class="table admin-table m-0">
          <thead>
            <tr>
              <th>Descrição</th>
              <th class="text-end">Valor</th>
              <th v-if="podeEditar" class="text-end" style="width:80px;">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(linha, index) in formData.itens" :key="index">
              <td class="fw-medium">{{ linha.descricao }}</td>
              <td class="text-end fw-bold">{{ formatarNumeroView(linha.valor) }}</td>
              <td v-if="podeEditar" class="text-end">
                <button type="button" class="btn-icon text-primary" @click="abrirModalItem(index)">
                  <i class="bi bi-pencil-fill"></i>
                </button>
                <button type="button" class="btn-icon text-danger" @click="removerItem(index)">
                  <i class="bi bi-trash-fill"></i>
                </button>
              </td>
            </tr>
            <tr v-if="formData.itens.length === 0">
              <td :colspan="podeEditar ? 3 : 2" class="text-center text-muted py-3">
                Nenhum item adicionado.
              </td>
            </tr>
          </tbody>
          <tfoot v-if="formData.itens.length > 0">
            <tr class="table-active fw-bold">
              <td class="text-end">TOTAL:</td>
              <td class="text-end">{{ formatarNumeroView(totalItens) }}</td>
              <td v-if="podeEditar"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- MODAL ITEM (reutilizando ModalFormulario) -->
    <ModalFormulario
      v-model="isModalItemOpen"
      :titulo="indiceItemEditando === -1 ? 'Adicionar Item' : 'Editar Item'"
      largura="modal-md"
      textoConfirmar="Salvar Item"
      @confirmar="salvarModalItem"
    >
      <div class="row g-3">
        <div class="col-md-12">
          <CampoInput
            id="modalDescricao"
            label="Descrição do Item"
            type="text"
            v-model="modalItemParaEdicao.descricao"
            required
            placeholder="Ex: AFRMM, Taxa Siscomex, Honorários..."
          />
        </div>
        <div class="col-md-6">
          <CampoInput
            id="modalValor"
            label="Valor"
            type="moeda"
            v-model="modalItemParaEdicao.valor"
            required
          />
        </div>
      </div>
    </ModalFormulario>
  </ModalFormularioV2>
</template>

<style scoped>
.solicitacao-adiantamento-modal {
  max-height: 65vh;
  overflow-y: auto;
}

.admin-table-container {
  overflow-x: auto;
}

.btn-icon {
  background: none;
  border: none;
  color: #6c7d8e;
  padding: 0.3rem;
  border-radius: 50%;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s;
}

.btn-icon:hover {
  background-color: #e2e6ea;
  color: #212934;
}

[data-bs-theme="dark"] .btn-icon:hover {
  background-color: #343a40;
  color: #fff;
}

.btn-icon.text-danger {
  color: #dc3545;
}

.btn-icon.text-danger:hover {
  background-color: rgba(220, 53, 69, 0.1);
  color: #b02a37;
}

/* (CSS do modal item inline removido — agora usa ModalFormulario) */
</style>
